import {
  OnInit,
  OnDestroy,
  inject,
  DestroyRef,
  Injector,
  output,
  computed,
  signal,
  Component,
  Signal,
  effect,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { FormUtils } from 'app/shared/form/form-utils';
import { ChapterService } from '../chapter/chapter.service';
import { FacetHooks } from '../utils/facet-hooks';
import { FacetCrudService } from './facet-crud.service';
import { MyHistoryUiService } from '../my-history-ui.service';
import { concatMap, map, mergeMap, tap } from 'rxjs/operators';
import { combineLatest, filter, from, Observable, of, switchMap } from 'rxjs';
import { HttpUtils } from 'app/shared/http-utils';
import { FacetFilterService } from '../facet/facet-filter.service';
import { FilesService } from '../../shared/files.service';
import { DialogService } from 'app/shared/dialog/dialog.service';
import { TextService } from 'app/shared/text.service';
import { YearsPeriodComponent } from 'app/shared/form/years-period/years-period.component';
import { FileResponse } from 'app/shared/file-response';
import { ValidationMessages } from 'app/shared/form/validation-massages';

@Component({
  standalone: true,
  template: '',
})
export abstract class FacetFormComponent<
    Model extends { id?: string | undefined },
    ModelForm extends { imageFile: File | File[]; suggestedImageFiles?: string; audio?: string | File },
    ModelSave extends { id?: string },
  >
  implements OnInit, OnDestroy
{
  abstract facetCrudService: FacetCrudService<Model>;
  abstract selected: Signal<Model | null>;
  abstract selectedImage: Signal<string | null | string[]>;
  abstract fileApiUrl: string;
  private firstRun = true;
  validationMessages = ValidationMessages;

  protected audioFileApiUrl: string = HttpUtils.getApiUrl('/files/audio');
  protected chapterService = inject(ChapterService);
  protected fb = inject(FormBuilder);
  protected destroyRef = inject(DestroyRef);
  protected uiService = inject(MyHistoryUiService);
  protected facetFilterService = inject(FacetFilterService);
  protected selectedFacet = this.chapterService.selectedFacet;
  protected selectedFacetType = this.chapterService.selectedFacetType;
  private injector = inject(Injector);
  protected filesService = inject(FilesService);
  private dialog = inject(DialogService);
  private text = inject(TextService);

  submitted = output<ModelSave | null>();
  mode = computed(() => {
    return this.selectedFacet() ? 'Edit' : 'Create';
  });
  saveButtonText = computed(() => (this.selectedFacet() ? 'Save' : 'Create'));
  isEditMode = computed(() => this.mode() === 'Edit');
  form: Signal<FormGroup> = computed(() => {
    const selected = this.selected();
    const imageFile = this.selectedImage();

    return this.fb.group({
      facets: this.fb.array([this.createFormGroup(selected, imageFile)]),
    });
  });
  facets: Signal<FormArray<FormGroup> | null> = computed(() => FormUtils.getFormArray(this.form(), 'facets'));
  facetId = signal<string | undefined>(undefined);

  abstract createFormGroup(facet: Model | null, imageFile?: string | null | string[], hasOngoing?: boolean): FormGroup;

  abstract getSubmitPayload(facet: ModelForm): ModelSave;

  constructor() {
    effect(() => {
      this.uiService.isSubmitRequested();
      if (this.firstRun) {
        this.firstRun = false;
        return;
      }
      const { formIsDirty } = FormUtils;
      if (!formIsDirty(this.form())) {
        this.uiService.closeAllDialogs();
        return;
      }

      this.dialog
        .confirm({
          title: '',
          secondTitle: this.text.get('save_dialog'),
          text: this.text.get('save_dialog_text'),
        })
        .afterClosed()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((res) => {
          if (!res) {
            this.uiService.closeAllDialogs();
            return;
          }
          this.onSubmit();
        });
    });
  }

  ngOnInit(): void {
    const selectedFacet = this.selectedFacet();
    if (selectedFacet) {
      this.facetCrudService.setSelected(selectedFacet.id);
    }
  }

  ngOnDestroy(): void {
    this.chapterService.setSelectedFacet(null);
    this.facetCrudService.setSelected(null);
  }

  onAddFacet() {
    const facetsArray = this.facets(); // get the FormArray
    const hasOngoing = facetsArray?.controls.find((control) => !!control.value.isOngoing);
    const newGroup = this.createFormGroup(null, null, !!hasOngoing); // or pass any values you want
    facetsArray?.push(newGroup);
  }

  onSubmit() {
    const { formValidityCheck, getFormArray } = FormUtils;
    if (!formValidityCheck(this.form())) {
      return;
    }

    const places = getFormArray(this.form(), 'facets');
    combineLatest(
      places.getRawValue().map((facet: ModelForm) => {
        return this.submitFormGroup(facet);
      }),
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((results) => this.onSubmitted(results[0]));
  }

  onSubmitted(facet: ModelSave) {
    this.submitted.emit(facet);
  }

  removeFacet(i: number) {
    this.facets()?.removeAt(i);
  }

  submitFormGroup(facet: ModelForm): Observable<ModelSave> {
    const suggestedImageFile = facet.suggestedImageFiles?.length ? facet.suggestedImageFiles[0] : null;
    const payload = this.getSubmitPayload(facet);

    return this.facetCrudService.save(payload, payload?.id).pipe(
      mergeMap((res) => {
        const requests: Observable<FileResponse | null>[] = [];
        const imageFile = suggestedImageFile ? suggestedImageFile : facet.imageFile;

        this.facetId.set(res.id);

        if (facet.audio && this.filesService.isBlobUrl(facet.audio as string)) {
          const audioUpload$ = this.filesService
            .blobToFile(facet.audio as string, `${Date.now()}.wav`)
            .pipe(
              switchMap((file) => FacetHooks.uploadFile(this.injector, res.id as string, file, this.audioFileApiUrl)),
            );

          requests.push(audioUpload$);
        }

        if (imageFile instanceof File || facet.suggestedImageFiles) {
          const uploadFileFromUrl = !!suggestedImageFile;

          const imageUpload$ = FacetHooks.uploadFile(
            this.injector,
            res.id as string,
            imageFile as File,
            this.fileApiUrl,
            uploadFileFromUrl,
          );

          requests.push(imageUpload$);
        }

        if (Array.isArray(imageFile)) {
          const imageArray = imageFile.filter((image) => image instanceof File);

          if (imageArray.length) {
            const imageUploads$ = from(imageArray).pipe(
              filter((image) => image instanceof File),
              concatMap((file) => FacetHooks.uploadFile(this.injector, res.id as string, file, this.fileApiUrl)),
            );

            requests.push(imageUploads$);
          }
        }

        if (!imageFile && !facet.suggestedImageFiles?.length && payload?.id) {
          const imageDelete$ = FacetHooks.removeFile(this.injector, res.id as string, this.fileApiUrl);
          requests.push(imageDelete$);
        }

        if (requests.length > 0) {
          return combineLatest(requests).pipe(map(() => payload));
        }
        return of(payload);
      }),
    );
  }

  getFormControl(controlName: string, formGroupIndex: number) {
    return this.facets()?.at(formGroupIndex)?.get(controlName);
  }

  onImageSelectionChange(image: File, i: number) {
    const suggestedImageFileControl = this.getFormControl('suggestedImageFiles', i);
    if (suggestedImageFileControl?.value?.length) {
      suggestedImageFileControl?.patchValue([], { emitEvent: false });
    }
  }

  onSuggestedSelectionChange(value: string[], i: number) {
    const imageFileControl = this.getFormControl('imageFile', i);
    if (imageFileControl?.value) {
      imageFileControl?.patchValue(null, { emitEvent: false });
    }
  }

  isOngoingChapterChange(index: number, isOngoing: boolean) {
    if (isOngoing) {
      this.facets()?.at(index).get('years.end')?.removeValidators(Validators.required);
      this.facets()?.at(index).get('years.end')?.disable({ emitEvent: false });
      this.facets()?.at(index).get('years.end')?.reset();
    } else {
      this.facets()?.at(index).get('years.end')?.enable({ emitEvent: false });
      this.facets()?.at(index).get('years.end')?.addValidators(Validators.required);
      // TODO startEndValidator
      // alias.setValidators(GroupValidator('start', 'years.end', (start: string, end: string) => +start <= +end, 'range'));
    }
    this.facets()?.at(index).get('years.end')?.updateValueAndValidity();
    // TODO startEndValidator
    // this.form?.updateValueAndValidity();
  }
}
